import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { Alert, Card, PageHeader, Spinner, useToast } from '@platform/ui';

import { CatalogList } from '../../features/catalog/catalog-list';
import { catalogKey } from '../../features/tenants/use-tenant';
import type { CreateCatalogEntry } from '../../lib/api/platform';
import { usePlatformClient } from '../../lib/api/platform-client-context';

export const Route = createFileRoute('/catalog/')({
  component: CatalogPage,
});

/** `/catalog` — the integrations catalog (MASTER_PLAN M3.8, Docs/API.md §2.13). */
function CatalogPage() {
  const client = usePlatformClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [error, setError] = useState<string | undefined>(undefined);

  const catalog = useQuery({ queryKey: catalogKey(), queryFn: () => client.listCatalog() });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: catalogKey() }),
      // A catalogue change shows up in every tenant's Integrations tab.
      queryClient.invalidateQueries({ queryKey: ['platform-tenant'] }),
    ]);
  };

  const create = useMutation({
    mutationFn: (input: CreateCatalogEntry) => client.createCatalogEntry(input),
    onSuccess: async (entry) => {
      setError(undefined);
      toast({ tone: 'success', message: `${entry.name} added to the catalog.` });
      await refresh();
    },
    onError: (cause: Error) => {
      setError(cause.message);
    },
  });

  const update = useMutation({
    /* `key` and `category` are dropped on the way out: moving either would re-point live
       installs at code that does not implement their contract (API.md §2.13). */
    mutationFn: ({ key, patch }: { key: string; patch: CreateCatalogEntry }) =>
      client.updateCatalogEntry(key, {
        name: patch.name,
        description: patch.description,
        vendor: patch.vendor,
        version: patch.version,
        capabilities: patch.capabilities,
        planFeatureKey: patch.planFeatureKey,
        docsUrl: patch.docsUrl,
        status: patch.status,
        settingsSchema: patch.settingsSchema,
        credentialSchema: patch.credentialSchema,
      }),
    onSuccess: async (entry) => {
      setError(undefined);
      toast({ tone: 'success', message: `${entry.name} updated.` });
      await refresh();
    },
    onError: (cause: Error) => {
      setError(cause.message);
    },
  });

  if (catalog.isPending) {
    return (
      <>
        <PageHeader title="Integration catalog" />
        <Card className="p-6">
          <Spinner label="Loading the catalog" />
        </Card>
      </>
    );
  }

  if (catalog.isError) {
    return (
      <>
        <PageHeader title="Integration catalog" />
        <Alert tone="error">The catalog could not be loaded. Try again in a moment.</Alert>
      </>
    );
  }

  return (
    <CatalogList
      entries={catalog.data}
      error={error}
      onCreate={(input) => {
        create.mutate(input);
      }}
      onUpdate={(key, patch) => {
        update.mutate({ key, patch });
      }}
    />
  );
}
